import {
  ActionStatusChip,
  DataGridDateFilter,
  DataGridIssueActionCountFilter,
  DataGridIssueSLAsFilter,
  DataGridIssueSubtypeFilter,
  DataGridIssueTypeFilter,
  DataGridUserFilter,
  DateWithTime,
  FieldWithTooltip,
  GridHeader,
  IssueStatusChip,
  type ExtendedGridColDef,
} from '@issuemanager/apps/client/components';
import { useLocalizedIssueTypes } from '@issuemanager/client/features/issues';
import {
  ISSUE_FIELD_ACTION_COUNT,
  ISSUE_FIELD_ACTION_PROGRESS,
  ISSUE_FIELD_APPROVER,
  ISSUE_FIELD_CREATED,
  ISSUE_FIELD_CREATOR,
  ISSUE_FIELD_DUE_DATE,
  ISSUE_FIELD_ISSUE_STATUS,
  ISSUE_FIELD_ISSUE_SUBTYPE,
  ISSUE_FIELD_ISSUE_TYPE,
  ISSUE_FIELD_MANAGER,
  ISSUE_FIELD_OWNER,
  ISSUE_FIELD_SEVERITY,
  ISSUE_FIELD_SLA_DUE_DAYS,
  ISSUE_FIELD_TITLE,
  ISSUE_FIELD_UPDATED,
  IssueType,
} from '@issuemanager/constants';
import type { DataGridProProps } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GroupingCell } from '../grouping-cell';
import { IssueActionProgressLabel } from '../issue-action-progress-label';
import { IssueGridSLAColumn } from '../issue-grid-sla-column';
import { IssueSeverityChip } from '../issue-severity-chip';
import { useIssueSourceApplications } from '../issue-source-app';
import { IssueGridActionProgressFilter } from './issue-grid-action-progress-filter';
import { IssueGridApproverFilter } from './issue-grid-approver-filter';
import { IssueGridCreatedFilter } from './issue-grid-created-filter';
import { IssueGridDueDateFilter } from './issue-grid-due-date-filter';
import { DataGridIssueEntityNameFilter } from './issue-grid-issue-entity-name-filter';
import { IssueGridIssueFilter } from './issue-grid-issue-filter';
import { IssueGridOwnerFilter } from './issue-grid-owner-filter';
import { IssueGridSeverityFilter } from './issue-grid-severity-filter';
import { IssueGridSourceApplicationFilter } from './issue-grid-source-app-filter';
import { IssueGridStatusFilter } from './issue-grid-status-filter';
import { IssueGridTitleFilter } from './issue-grid-title-filter';

const commonIssueGridColumnsConfig = {
  minWidth: 150,
};

export const useIssueGridColumns = () => {
  const { t } = useTranslation(['issue']);
  const localizedIssueTypes = useLocalizedIssueTypes();
  const sourceApplications = useIssueSourceApplications();

  const column = useCallback(
    (props: ExtendedGridColDef) => ({
      renderHeader: GridHeader,
      ...commonIssueGridColumnsConfig,
      ...props,
    }),
    [],
  );

  const groupingColDef: DataGridProProps['groupingColDef'] = useMemo(
    () =>
      column({
        field: '__tree_data_group__',
        headerName: t('issue:list.table.header.code'),
        renderHeaderFilter: IssueGridIssueFilter,
        renderCell: (params) => <GroupingCell {...params} />,
      }),
    [],
  );

  const columns = useMemo(() => {
    const columns = [
      column({
        field: ISSUE_FIELD_TITLE,
        headerName: t('issue:list.table.header.title'),
        renderHeaderFilter: IssueGridTitleFilter,
        renderCell: (params) => <FieldWithTooltip value={params.value} />,
        minWidth: 300,
      }),
      column({
        field: ISSUE_FIELD_SEVERITY,
        headerName: t('issue:list.table.header.severity'),
        renderCell: (params) => <IssueSeverityChip severity={params.value} />,
        renderHeaderFilter: IssueGridSeverityFilter,
        minWidth: 200,
      }),
      column({
        field: ISSUE_FIELD_ISSUE_STATUS,
        headerName: t('issue:list.table.header.status'),
        renderCell: (params) => {
          if (params.value === undefined) return null;

          if (params.rowNode.depth === 1) {
            return <ActionStatusChip status={params.value} />;
          } else {
            return <IssueStatusChip status={params.value} />;
          }
        },
        renderHeaderFilter: IssueGridStatusFilter,
        minWidth: 200,
      }),
      column({
        field: ISSUE_FIELD_ACTION_COUNT,
        headerName: t('issue:list.table.header.actions'),
        renderCell: (params) => params.value,
        renderHeaderFilter: DataGridIssueActionCountFilter,
      }),
      column({
        field: ISSUE_FIELD_ACTION_PROGRESS,
        headerName: t('issue:list.table.header.actionProgress'),
        renderCell: (params) => (
          <IssueActionProgressLabel value={params.value} />
        ),
        renderHeaderFilter: IssueGridActionProgressFilter,
        minWidth: 200,
      }),
      column({
        field: ISSUE_FIELD_ISSUE_TYPE,
        headerName: t('issue:list.table.header.type'),
        valueGetter: (params) =>
          localizedIssueTypes[params.row.issueType?.name as IssueType] ??
          params.row.issueType?.name,
        renderHeaderFilter: DataGridIssueTypeFilter,
      }),
      column({
        field: ISSUE_FIELD_ISSUE_SUBTYPE,
        headerName: t('issue:list.table.header.subtype'),
        valueGetter: (params) => params.row.issueSubtype?.name,
        renderHeaderFilter: DataGridIssueSubtypeFilter,
      }),
      column({
        field: 'sourceApplication',
        headerName: t('issue:list.table.header.sourceApp'),
        valueGetter: (params) =>
          sourceApplications.find((app) => app.value === params.row.source?.app)
            ?.label ?? '',
        renderHeaderFilter: IssueGridSourceApplicationFilter,
        minWidth: 200,
      }),
      column({
        field: 'sourceEntityName',
        headerName: t('issue:list.table.header.sourceEntity'),
        valueGetter: (params) => params.row.source?.name,
        renderHeaderFilter: DataGridIssueEntityNameFilter,
        minWidth: 200,
      }),
      column({
        field: ISSUE_FIELD_CREATED,
        headerName: t('issue:list.table.header.dateAdded'),
        renderCell: (params) => <DateWithTime date={params.value} />,
        renderHeaderFilter: IssueGridCreatedFilter,
      }),
      column({
        field: ISSUE_FIELD_DUE_DATE,
        headerName: t('issue:list.table.header.dueDate'),
        renderCell: (params) => <DateWithTime date={params.value} />,
        renderHeaderFilter: IssueGridDueDateFilter,
      }),
      column({
        field: ISSUE_FIELD_UPDATED,
        headerName: t('issue:list.table.header.lastActivity'),
        renderCell: (params) => <DateWithTime date={params.value} />,
        renderHeaderFilter: DataGridDateFilter,
      }),
      column({
        field: ISSUE_FIELD_SLA_DUE_DAYS,
        headerName: t('issue:list.table.header.sla'),
        renderCell: (params) => <IssueGridSLAColumn value={params.value} />,
        renderHeaderFilter: DataGridIssueSLAsFilter,
      }),
      column({
        field: ISSUE_FIELD_CREATOR,
        headerName: t('issue:list.table.header.addedBy'),
        valueGetter: (params) => params.value?.name,
        renderHeaderFilter: DataGridUserFilter,
      }),
      column({
        field: ISSUE_FIELD_MANAGER,
        headerName: t('issue:list.table.header.manager'),
        valueGetter: (params) => params.value?.name,
        renderHeaderFilter: DataGridUserFilter,
      }),
      column({
        field: ISSUE_FIELD_OWNER,
        headerName: t('issue:list.table.header.owner'),
        valueGetter: (params) => params.value?.name,
        renderHeaderFilter: IssueGridOwnerFilter,
      }),
      column({
        field: ISSUE_FIELD_APPROVER,
        headerName: t('issue:list.table.header.approver'),
        valueGetter: (params) => params.value?.name,
        renderHeaderFilter: IssueGridApproverFilter,
      }),
    ];

    return columns;
  }, [column, localizedIssueTypes, sourceApplications, t]);

  return {
    columns,
    groupingColDef,
  };
};
